import { listarVariaveisObservacao, selecionarFormulasPorCriterio } from '@shared/components/formula/utils/formula-contexto.util';
import { coletarEstadosFormulas } from '@shared/components/formula/utils/formula-estados.util';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { CofinsTributoObservacoesSectionComponent } from './sections/tributo-observacoes-section/tributo-observacoes-section.component';
import { CofinsTributoObservacoesFiscoSectionComponent } from './sections/tributo-observacoes-fisco-section/tributo-observacoes-fisco-section.component';
import { MAPA_CAMPOS_COFINS_NATUREZA, CofinsTributoRegraSectionComponent } from './sections/tributo-regra-section/tributo-regra-section.component';
import { CofinsTributoExcecoesSectionComponent } from './sections/tributo-excecoes-section/tributo-excecoes-section.component';
import { FormulaEditorDialogComponent, FormulaEditorDialogData, FormulaEditorDialogResult } from '@shared/components/formula/formula-editor-dialog/formula-editor-dialog.component';
import { VariaveisDisponiveisDialogComponent, VariaveisDisponiveisDialogData, VariaveisCursorPayload } from '@shared/components/formula/variaveis-disponiveis-dialog/variaveis-disponiveis-dialog.component';
import { NaturezaOperacaoCofinsExcecaoPendenteStruct } from '@shared/services/structs/motor-fiscal/natureza-operacao-cofins-excecao-pendente.struct';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { CstCofinsStruct } from '@shared/services/structs/motor-fiscal/cst-cofins.struct';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { planejarTrocaCodigoFormulas } from '@shared/components/formula/utils/troca-codigo-formulas.util';
import {
    aplicarObrigatoriedadeIndicadores,
    controlesDasRegrasEntreCampos,
    lerIndicadoresCst,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';

@Component({
    selector: 'app-natureza-operacao-cofins-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AtivacaoSectionCardComponent,
        CofinsTributoRegraSectionComponent,
        CofinsTributoObservacoesSectionComponent,
        CofinsTributoObservacoesFiscoSectionComponent,
        CofinsTributoExcecoesSectionComponent,
    ],
    templateUrl: './natureza-operacao-cofins-tab.component.html',
    styleUrl: './natureza-operacao-cofins-tab.component.css',
})
export class NaturezaOperacaoCofinsTabComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;
    @Input() codigoTributos: CstCofinsStruct[] = [];
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() formulasCofins: FormulaCatalogoItemStruct[] = [];
    @Input() estadosFormulas: FormulaItemStruct[] = [];
    @Input() idNaturezaOperacao: number | null = null;
    @Input() excecoesPendentes: NaturezaOperacaoCofinsExcecaoPendenteStruct[] = [];
    @Output() excecoesChange = new EventEmitter<NaturezaOperacaoCofinsExcecaoPendenteStruct[]>();

    private codigoTributoAnterior: string | null = null;
    private restaurandoCodigoTributo = false;

    ngOnInit(): void {
        this.isAtivoControl.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.codigoTributoAnterior = this.cstCofinsValue;
        this.cofinsForm.get('cstCofins')?.valueChanges.subscribe(codigo => {
            this.sincronizarObrigatoriedade();
            this.confirmarTrocaCodigoTributo(codigo);
        });
        controlesDasRegrasEntreCampos(MAPA_CAMPOS_COFINS_NATUREZA).forEach(nome =>
            this.cofinsForm.get(nome)?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade()));
        this.sincronizarObrigatoriedade();
    }

    /** CST exigido com o tributo ligado; os campos do código, pelo indicador do catálogo. */
    private sincronizarObrigatoriedade(): void {
        const ativo = !!this.isAtivoControl.value;
        const codigo = this.cofinsForm.get('cstCofins')?.value;
        this.aplicarObrigatorio(this.cofinsForm.get('cstCofins'), ativo);
        const indicadores = ativo && codigo ? lerIndicadoresCst(this.codigoTributos.find(c => c.codigo === codigo)?.indicadores) : null;
        aplicarObrigatoriedadeIndicadores(this.cofinsForm, indicadores, MAPA_CAMPOS_COFINS_NATUREZA);
    }

    private aplicarObrigatorio(control: import('@angular/forms').AbstractControl | null | undefined, obrigatorio: boolean): void {
        if (!control) return;
        if (obrigatorio) {
            control.addValidators(Validators.required);
        } else {
            control.removeValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    public get cofinsForm(): FormGroup {
        return this.form.get('cofins') as FormGroup;
    }

    public get isAtivoControl(): FormControl {
        return this.cofinsForm.get('isAtivo') as FormControl;
    }

    public get cstCofinsValue(): string | null {
        return this.cofinsForm?.get('cstCofins')?.value ?? null;
    }

    /** Tipo de operação da natureza (Entrada/Saída), lido do form raiz. */
    public get tipoOperacaoValue(): TipoOperacaoEnum {
        return this.form?.get('tipoOperacao')?.value ?? TipoOperacaoEnum.Saida;
    }

    public get formulasParaCodigoTributo(): FormulaCatalogoItemStruct[] {
        return this.obterFormulasParaCodigo(this.cstCofinsValue);
    }

    private obterFormulasParaCodigo(codigo: string | null): FormulaCatalogoItemStruct[] {
        if (!codigo) return this.formulasCofins;
        return selecionarFormulasPorCriterio(this.formulasCofins, 'cst_cofins', codigo);
    }

    /** Fonte única em memória das fórmulas: o FormGroup, que o salvar() também lê.
        O @Input estadosFormulas serve só de seed inicial (o pai já popula o control). */
    private get estadosFormulasMemoria(): FormulaItemStruct[] {
        return (this.cofinsForm.get('_estadosFormulas')?.value as FormulaItemStruct[]) ?? [];
    }

    public get personalizadasCount(): number {
        return this.estadosFormulasMemoria.filter(e => e.tipo === TipoFormulaEnum.Personalizada).length;
    }

    /** Variáveis únicas (por tag) disponíveis nas fórmulas do SN. */
    public get variaveisUnicas(): VariavelStruct[] {
        return listarVariaveisObservacao(this.formulasParaCodigoTributo);
    }

    constructor(
        private dialog: MatDialog,
        private formulaCatalogoService: FormulaCatalogoService,
    ) { }

    private confirmarTrocaCodigoTributo(novoCodigo: string | null): void {
        if (this.restaurandoCodigoTributo || novoCodigo === this.codigoTributoAnterior) return;
        const plano = planejarTrocaCodigoFormulas(this.estadosFormulasMemoria,
            this.obterFormulasParaCodigo(this.codigoTributoAnterior), this.obterFormulasParaCodigo(novoCodigo), {
            observacao: this.cofinsForm.get('observacao')?.value,
            observacaoFisco: this.cofinsForm.get('observacaoFisco')?.value,
        });
        if (plano.estadosParaRestaurar.length === 0 && plano.variaveisPendentes.length === 0) {
            this.codigoTributoAnterior = novoCodigo;
            return;
        }

        const partes = [
            plano.estadosParaRestaurar.length > 0 ? `restaurará ${plano.estadosParaRestaurar.length} configuraç${plano.estadosParaRestaurar.length === 1 ? 'ão' : 'ões'} de fórmulas` : null,
            plano.variaveisPendentes.length > 0 ? `deixará pendentes as variáveis [${plano.variaveisPendentes.map(nome => `{${nome}}`).join(', ')}] nas observações` : null,
        ].filter(Boolean).join(' e ');
        this.dialog.open(ConfirmModalComponent, {
            data: { title: 'Confirmar troca de CST', description: `A troca para CST ${novoCodigo ?? 'não informado'} ${partes}. Deseja continuar?`, buttonConfirm: 'Continuar', buttonClose: 'Cancelar' },
        }).afterClosed().subscribe(result => {
            if (!result?.confirm) {
                this.restaurandoCodigoTributo = true;
                this.cofinsForm.get('cstCofins')?.setValue(this.codigoTributoAnterior, { emitEvent: false });
                this.restaurandoCodigoTributo = false;
                this.sincronizarObrigatoriedade();
                return;
            }
            this.cofinsForm.get('_estadosFormulas')?.setValue(this.estadosFormulasMemoria.filter(estado => !plano.estadosParaRestaurar.includes(estado)));
            Object.entries(plano.observacoes).forEach(([campo, valor]) => this.cofinsForm.get(campo)?.setValue(valor));
            this.codigoTributoAnterior = novoCodigo;
        });
    }

    public abrirEditorFormulas(): void {
        const codigoTributo = this.codigoTributos.find(c => c.codigo === this.cstCofinsValue);
        const data: FormulaEditorDialogData = {
            titulo: ['COFINS', this.cstCofinsValue, codigoTributo?.descricao].filter(Boolean).join(' - '),
            formulas: this.formulasParaCodigoTributo.map(formula => ({
                ...formula,
                variaveis: (formula.variaveisPermitidas ?? formula.variaveis),
            })),
            estadosIniciais: this.estadosFormulasMemoria,
            estadosFormulasAgregados: coletarEstadosFormulas(this.form),
            contextoRaiz: 'natureza-operacao',
            validarPersonalizacoes: request => this.formulaCatalogoService.validarPersonalizacoes(request),
        };

        const ref = this.dialog.open(FormulaEditorDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((result: FormulaEditorDialogResult | null) => {
            if (result) {
                this.cofinsForm.get('_estadosFormulas')?.setValue(result.formulas);
            }
        });
    }

    public abrirVariaveisDisponiveis(payload: VariaveisCursorPayload): void {
        const editor = payload.editor;
        const data: VariaveisDisponiveisDialogData = {
            variaveis: this.variaveisUnicas,
            targetControl: payload.control,
            cursorStart: payload.selectionStart,
            cursorEnd: payload.selectionEnd,
            onInsert: editor ? (v: VariavelStruct) => editor.inserirVariavel(v) : undefined,
        };

        this.dialog.open(VariaveisDisponiveisDialogComponent, {
            width: '820px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            data,
        });
    }
}
